import { ISafeExecuteAsyncAction } from '../../../typings/stores';
import { TagEditorMode } from '../../../typings/tags/tagEditorMode';
import TagItem from '../../../typings/tags/tagItem';
import ITagsFetcher from '../../../typings/tags/tagsFetcher';
import { TagsSortOrder } from '../../../typings/tags/tagsSortOrder';
import DeleteTagListItem from '../../lists/deleteTagListItem';
import RenameTagListItem from '../../lists/renameTagListItem';
import ControlContainer from '../controlContainer';
import ExistingTagsAddButton from '../existingTagsAddButton';
import ExistingTagsItem from '../existingTagsItem';
import ExistingTagsLoader from '../existingTagsLoader';
import ExistingTagsNoItems from '../existingTagsNoItems';
import SortDropdown from '../sortDropdown';
import { Stack } from '@mui/material';
import Box from '@mui/material/Box';
import { Colors } from 'copyright-clearance-center-common-ui';
import { useStateRef } from 'copyright-clearance-center-common-ui-react';
import { uniqWith } from 'lodash';
import { useEffect, useRef, useState } from 'react';
import { VList } from 'virtua';

export interface IAvailableTag {
	id: number;
	tag: string;
	label: string;
	new?: boolean;
	selected?: boolean;
}

export interface IAvailableTagsProps {
	fetcher: ITagsFetcher;
	onPendingTagsAdd?: (value?: string) => void;
	onSelect?: (tag: string) => void;
	header?: string;
	height?: string;
	width?: string;
	testId?: string;
	safeExecuteAsync: ISafeExecuteAsyncAction;
	tagsEqual: (tag1: string, tag2: string) => boolean;
	tagIncludes: (tag: string, filter: string) => boolean;
	tagEditorMode: TagEditorMode;
	onSortOrderChange: (newSort: TagsSortOrder) => void;
	sortOrder: TagsSortOrder;
	onExistingTagRemove?: (tag: IAvailableTag) => void;
	onExistingTagUpdate?: (tag: IAvailableTag, updatedName: string) => void;
}

export type AvailableTagsControlHook = [
	control: JSX.Element,
	currentTags: IAvailableTag[],
	newTags: string[],
	setNewTags: (tags: string[]) => void,
	setSelectedTags: (tags: string[]) => void,
	setPendingTagsValue: (value: number | string) => void,
	setFetcher: (fetcher: ITagsFetcher) => void,
	isLoading: boolean,
	isTagInAction: boolean
];

type TagAction = 'update' | 'remove';

const useAvailableTags = (props: IAvailableTagsProps): AvailableTagsControlHook => {
	const [fetcher, setFetcher] = useState(props.fetcher);
	const [existingTags, setExistingTags, existingTagsRef] = useStateRef<IAvailableTag[]>([]);
	const [newTags, setNewTags] = useState<string[]>([]);
	const [selectedTags, setSelectedTags] = useState<string[]>([]);
	const [availableTags, setAvailableTags] = useState<IAvailableTag[]>([]);
	const [pendingTagsValue, setPendingTagsValue] = useState<number | string>('');

	const [isNextPageLoading, setNextPageLoading] = useState(false);
	const [isReseting, setIsReseting] = useState(false);

	const hasMountedRef = useRef(false);
	const fetchedCountRef = useRef(-1);
	const hasNextPage = useRef(false);
	const stateVersion = useRef(-1);

	const [tagInAction, setTagInAction] = useState<{ tag: string; action: TagAction } | null>(null);

	const indentBeforeRefetch = Math.ceil(fetcher.pageSize / 2);
	const isLoading = isNextPageLoading || isReseting;
	const hasNewTags = newTags.length > 0;
	const editable = props.tagEditorMode === TagEditorMode.Full;

	const isTagInAction = tagInAction !== null;

	useEffect(() => {
		if (hasMountedRef.current) {
			loadTags(true);
		}

		hasMountedRef.current = true;
	}, [fetcher]);

	useEffect(() => {
		const result: IAvailableTag[] = [
			...newTags
				.map(newTag => ({
					id: TagItem.NewTagId,
					tag: newTag,
					label: newTag,
					new: true,
					selected: !!selectedTags.some(t => props.tagsEqual(t, newTag))
				}))
				.filter(t => props.tagIncludes(t.tag, fetcher.filter)),
			...existingTags.map(existingTag => ({
				...existingTag,
				selected: !!selectedTags.some(t => props.tagsEqual(t, existingTag.tag))
			}))
		];
		setAvailableTags(uniqWith(result, (t1, t2) => props.tagsEqual(t1.tag, t2.tag)));
	}, [newTags, existingTags, selectedTags]);

	useEffect(() => {
		setTagInAction(null);
	}, [newTags]);

	const loadTags = async (reset = false) => {
		if (!reset && isReseting) {
			return;
		}

		const currVersion = ++stateVersion.current;

		if (reset) {
			setIsReseting(true);
			setExistingTags([]);
			setAvailableTags([]);
			hasNextPage.current = true;
		} else {
			setNextPageLoading(true);
		}

		const newItems = await props.safeExecuteAsync(() => fetcher.fetchNextPage(), null);

		if (currVersion !== stateVersion.current || (!reset && isReseting)) {
			return;
		}

		if (newItems) {
			const tagList = [...existingTagsRef.current, ...newItems.tags];
			hasNextPage.current = newItems.hasNextPage;
			setExistingTags(uniqWith(tagList, (t1, t2) => props.tagsEqual(t1.tag, t2.tag)));
		} else {
			hasNextPage.current = false;
		}

		if (reset) {
			fetchedCountRef.current = -1;
			setIsReseting(false);
		}

		setNextPageLoading(false);
	};

	const removeNewTag = (tag: string) => {
		setNewTags(newTags.filter(t => !props.tagsEqual(t, tag)));
	};

	const removeExistingTag = (tag: string) => {
		setExistingTags(existingTags.filter(t => !props.tagsEqual(t.tag, tag)));
	};

	function isTagItemInteractive(tag: IAvailableTag) {
		return !hasNewTags || tag.new;
	}

	function validateTagOnUpdate(oldName: string, newName: string): string | null {
		const tagName = newName.trim();
		if (tagName.length === 0) {
			return localize('Edit the tag name and then select Done to update the tag.');
		}

		const isDuplicateName =
			!props.tagsEqual(tagName, oldName) && !!availableTags.find(tag => props.tagsEqual(tag.tag, tagName));

		if (isDuplicateName) {
			return localize('This tag is a duplicate. Please select another name');
		}

		return null;
	}

	function handleTagRemove(): void {
		setTagInAction(null);
		if (hasNewTags) {
			removeNewTag(tagInAction.tag);
		} else {
			const removedTag = existingTags.find(t => props.tagsEqual(t.tag, tagInAction.tag));
			removeExistingTag(tagInAction.tag);
			props.onExistingTagRemove && props.onExistingTagRemove(removedTag);
		}
	}

	function handleTagUpdate(updatedName: string): void {
		setTagInAction(null);
		updatedName = updatedName.trim();
		if (hasNewTags) {
			updateNewTag(tagInAction.tag, updatedName);
		} else {
			updateExistingTag(tagInAction.tag, updatedName);
		}
	}

	function updateNewTag(tag: string, updatedName: string): void {
		const tagIndex = newTags.indexOf(tag);
		if (tagIndex === -1) {
			return;
		}

		newTags[tagIndex] = updatedName;
		setNewTags([...newTags]);
	}

	function updateExistingTag(tag: string, updatedName: string): void {
		const tagIndex = existingTags.findIndex(existingTag => props.tagsEqual(existingTag.tag, tag));
		const tagToUpdate = existingTags[tagIndex];
		if (props.tagsEqual(tagToUpdate.tag, updatedName)) {
			return;
		}

		existingTags[tagIndex] = {
			...tagToUpdate,
			tag: updatedName,
			label: tagToUpdate.label.replace(tag, updatedName)
		};

		setExistingTags([...existingTags]);
		props.onExistingTagUpdate && props.onExistingTagUpdate(tagToUpdate, updatedName);
	}

	function getAvailableTagsListItems() {
		return availableTags.map((item, key) => {
			const tagIsNotInAction = !props.tagsEqual(tagInAction?.tag, item.tag);
			if (tagIsNotInAction) {
				const tagActionsEnabled = isTagItemInteractive(item);

				return (
					<ExistingTagsItem
						key={key}
						editable={editable}
						onSelect={props.tagEditorMode !== TagEditorMode.NoAssign ? props.onSelect : null}
						onRemove={tagActionsEnabled ? tag => setTagInAction({ tag: tag, action: 'remove' }) : null}
						onUpdate={tagActionsEnabled ? tag => setTagInAction({ tag: tag, action: 'update' }) : null}
						{...item}
					/>
				);
			}

			return (
				<Stack
					key={key}
					direction="row"
					justifyContent="space-between"
					sx={{
						':hover': { backgroundColor: Colors.neutral100 },
						overflow: 'hidden',
						maxHeight: '100%',
						padding: '0.5rem 0.75rem'
					}}
				>
					{tagInAction.action === 'remove' && (
						<DeleteTagListItem
							name={item.tag}
							textMessage={localize(
								'Deleting a tag unassigns it from all documents and removes it from the list of available tags.'
							)}
							onCancel={() => setTagInAction(null)}
							onDone={handleTagRemove}
						/>
					)}
					{tagInAction.action === 'update' && (
						<RenameTagListItem
							name={item.tag}
							helperText={localize('Edit the tag name and then select Done to update the tag.')}
							onCancel={() => setTagInAction(null)}
							onValidation={newName => validateTagOnUpdate(item.tag, newName)}
							onDone={handleTagUpdate}
						/>
					)}
				</Stack>
			);
		});
	}

	const control = (
		<ControlContainer
			header={props.header ?? localize('Select tags to assign to items')}
			action={
				<SortDropdown
					onSortOrderChange={newSort => props.onSortOrderChange(newSort)}
					sortOrder={props.sortOrder}
				/>
			}
			testId={props.testId ?? 'available-tags'}
			height={props.height ?? '19rem'}
			width={props.width ?? '22.75rem'}
		>
			<Stack alignSelf="stretch" height="100%">
				{props.tagEditorMode === TagEditorMode.Full && (
					<ExistingTagsAddButton value={pendingTagsValue} onClick={props.onPendingTagsAdd} />
				)}
				<Box flex="1 1 auto">
					<VList
						onRangeChange={async (_, end) => {
							const tagsCount = availableTags.length;
							if (
								end >= 0 &&
								end + indentBeforeRefetch > tagsCount &&
								fetchedCountRef.current < tagsCount &&
								hasNextPage.current
							) {
								fetchedCountRef.current = tagsCount;
								await loadTags();
							}
						}}
					>
						{availableTags.length || isLoading ? getAvailableTagsListItems() : <ExistingTagsNoItems />}
						{isLoading && <ExistingTagsLoader />}
					</VList>
				</Box>
			</Stack>
		</ControlContainer>
	);

	return [
		control,
		existingTags,
		newTags,
		setNewTags,
		setSelectedTags,
		setPendingTagsValue,
		setFetcher,
		isLoading,
		isTagInAction
	];
};

export default useAvailableTags;
